import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Upload, Sparkles, Presentation, X, ArrowRight, GraduationCap } from "lucide-react";

const STEPS = [
  {
    n: 1,
    icon: Upload,
    title: "Téléversez une convention",
    subtitle: "PDF, DOCX ou XLSX — jusqu'à 200 Mo",
    body:
      "Créez un projet, indiquez le pays et le secteur (mines, pétrole, gaz, maritime, forêt), " +
      "puis glissez-déposez le(s) document(s) à analyser. GPT-4o extrait automatiquement plus de " +
      "60 champs juridiques, financiers et opérationnels.",
    color: "#1B4332",
  },
  {
    n: 2,
    icon: Sparkles,
    title: "Lancez les analyses",
    subtitle: "5 dimensions : financier, environnement, social, souveraineté, déséquilibre",
    body:
      "Cliquez sur « Lancer les analyses » — l'IA confronte la convention aux normes ONU, IFC, " +
      "Charte africaine, Vision minière africaine, plus votre droit national via la BLN. " +
      "Le dashboard regroupe 14 visualisations (cartes, sankey, radar, timeline).",
    color: "#D4A017",
  },
  {
    n: 3,
    icon: Presentation,
    title: "Présentez et agissez",
    subtitle: "Mode Présentation 9 slides · REJD 8 parties · Verdict partageable QR",
    body:
      "Exportez un REJD défendable devant la Cour des comptes, ou activez le Mode Présentation " +
      "plein écran pour un briefing député. Utilisez le Mode Pédagogie pour former vos équipes " +
      "sur les clauses abusives et leurs alternatives.",
    color: "#1A3C5E",
  },
];

export default function OnboardingTour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/onboarding/state")
      .then(({ data }) => {
        if (!cancelled && !data.onboarding_done) {
          // Laisser un moment au dashboard de se charger
          setTimeout(() => setOpen(true), 800);
        }
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  const close = async () => {
    setDismissing(true);
    try {
      await api.post("/onboarding/complete");
    } catch {
      /* silent */
    } finally {
      setOpen(false);
      setDismissing(false);
    }
  };

  const next = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else close();
  };
  const prev = () => step > 0 && setStep(step - 1);

  if (!open) return null;

  const s = STEPS[step];
  const Icon = s.icon;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in-0"
      style={{ background: "rgba(13, 27, 18, 0.85)", backdropFilter: "blur(8px)" }}
      data-testid="onboarding-overlay"
    >
      <div
        className="w-full max-w-2xl rounded-sm border p-8 md:p-10 shadow-2xl relative"
        style={{
          background: "hsl(var(--card))",
          borderColor: "rgba(212, 160, 23, 0.4)",
          boxShadow: "0 40px 80px rgba(0,0,0,0.5)",
        }}
      >
        {/* Close */}
        <button
          onClick={close}
          className="absolute top-3 right-3 p-2 opacity-60 hover:opacity-100 transition-opacity"
          data-testid="onboarding-close"
          aria-label="Fermer"
        >
          <X size={18} />
        </button>

        {/* Kicker */}
        <div className="flex items-center gap-2 mb-4 font-mono text-[10px] uppercase tracking-[0.3em] opacity-70">
          <GraduationCap size={12} style={{ color: "#D4A017" }} />
          Bienvenue — prise en main en 3 étapes
        </div>

        {/* Big icon */}
        <div
          className="w-16 h-16 rounded-sm flex items-center justify-center mb-5"
          style={{ background: `${s.color}15` }}
        >
          <Icon size={32} style={{ color: s.color }} />
        </div>

        {/* Title & body */}
        <h2 className="font-display text-3xl font-bold mb-1">
          <span className="font-mono opacity-50 mr-3">{s.n}/3</span>
          {s.title}
        </h2>
        <div className="text-sm opacity-70 mb-4 font-mono">{s.subtitle}</div>
        <p className="text-base opacity-90 leading-relaxed mb-8">{s.body}</p>

        {/* Dots + actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className="h-1 rounded-full transition-all"
                style={{
                  width: i === step ? "28px" : "12px",
                  background: i === step ? "#D4A017" : "hsl(var(--border))",
                }}
                aria-label={`Étape ${i + 1}`}
                data-testid={`onboarding-dot-${i}`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={close}
              disabled={dismissing}
              className="text-xs opacity-60 hover:opacity-100 mr-2"
              data-testid="onboarding-skip"
            >
              Passer
            </button>
            {step > 0 && (
              <Button
                onClick={prev}
                variant="outline"
                size="sm"
                className="rounded-sm"
                data-testid="onboarding-prev"
              >
                Retour
              </Button>
            )}
            <Button
              onClick={next}
              disabled={dismissing}
              size="sm"
              className="rounded-sm font-semibold"
              style={{ background: s.color, color: "white" }}
              data-testid="onboarding-next"
            >
              {step === STEPS.length - 1 ? "Commencer" : "Suivant"}
              <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
